import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

type ToastType = 'info' | 'success' | 'error' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

interface ShowToastOptions {
  /** Suppress identical messages within dedupeMs */
  dedupeKey?: string;
  dedupeMs?: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, options?: ShowToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const toastClass: Record<ToastType, string> = {
  info: 'toast-info',
  success: 'toast-success',
  error: 'toast-error',
  warning: 'toast-warning',
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const recentKeys = useRef<Map<string, number>>(new Map());

  const showToast = useCallback((message: string, type: ToastType = 'info', options?: ShowToastOptions) => {
    const key = options?.dedupeKey || `${type}:${message}`;
    const windowMs = options?.dedupeMs ?? (options?.dedupeKey ? 4000 : 0);
    if (windowMs > 0) {
      const last = recentKeys.current.get(key) || 0;
      if (Date.now() - last < windowMs) {
        return;
      }
      recentKeys.current.set(key, Date.now());
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div
        className="fixed bottom-6 right-4 sm:right-6 z-[200] flex flex-col gap-2 pointer-events-none max-w-sm w-full"
        aria-live="polite"
        aria-relevant="additions"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className={`${toastClass[toast.type]} pointer-events-auto`}>
            <p className="text-sm flex-1 leading-snug">{toast.message}</p>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              className="btn-icon p-1 shrink-0 opacity-70 hover:opacity-100"
              aria-label="Dismiss notification"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
};

export default ToastContext;
